import {defineStore} from 'pinia';
import {useFilterStore} from "./filter.ts";
import type {Emitter} from "mitt";
import mitt from "mitt";
import type {Ref, UnwrapNestedRefs} from "vue";
import {reactive, ref, watch} from "vue";
import type {AxiosRequestConfig} from "axios";
import {api} from "../scripts/api";
import type {ProductResponse} from "../../responses/ProductResponse";
import type {Pagination} from "../../types/Pagination.ts";
import {useLanguageStore} from "../../backend/stores/languageStore.ts";
import type {ArticleResponse} from "../../responses/ArticleResponse.ts";
import type {FilterResponse} from "../../responses/FilterResponse";
import type {ProductVariantResponse} from "../../responses/ProductVariantResponse";
import {reject} from "lodash";
import {label} from "../mixins/translations.ts";

type Events = {
    updatedFilter: void
    initPriceFilter: void
    selectedVariantChanged: ProductVariantResponse
    goToReviews: void
    quantityChanged: number
}

export type ProductLayoutType = "grid"|"list"

interface CustomProductResponseWithFilters extends Pagination<ProductResponse> {
    maxPrice: number
    currentPrice: string
    filters: FilterResponse[]
}

export const useProductStore = defineStore('product', () => {
    const filterStore = useFilterStore();
    const languageStore = useLanguageStore();

    const events: Emitter<Events> = mitt<Events>()
    const products: Ref<ProductResponse[]> = ref([]);
    const defaultValues = {
        page: 1,
        sort: 'order|asc'
    };
    const sort: Ref<string> = ref(defaultValues.sort);
    const page: Ref<number> = ref(defaultValues.page);
    const perPage: Ref<number> = ref(24);
    const perPageOptions: Ref<Array<number>> = ref([
        12,
        24,
        36
    ]);
    const productCount: Ref<number> = ref(0);
    const categorySlug: Ref<string> = ref('');
    const categoryName: Ref<string> = ref(label('site.product.search.placeholder.default'));
    const priceMax: Ref<number> = ref(100);
    const priceMin: Ref<number> = ref(0);
    const layout: Ref<ProductLayoutType> = ref("grid");
    const currentPrice: Ref<string> = ref("");
    const selectedVariant: Ref<ProductVariantResponse> = ref(null);
    const filterOnly: Ref<boolean> = ref(false);

    const getProductRequest: UnwrapNestedRefs<{
        timeout: number | null
        abort: AbortController | null
    }> = reactive({
        timeout: null,
        abort: null,
    })
    const getPropertiesRequest: UnwrapNestedRefs<{
        timeout: number | null
        abort: AbortController | null
    }> = reactive({
        timeout: null,
        abort: null,
    })
    const hasMoreResults = () => {
        return productCount.value >= (page.value * perPage.value);
    }

    const getProducts = async () => {
        return new Promise(async (resolve, reject) => {
            if (getProductRequest.abort !== null) {
                getProductRequest.abort.abort()
            }

            getProductRequest.abort = new AbortController()

            try {
                const sortParams: {[key: string]: string} = {};
                // if(defaultValues.page !== page.value) sortParams['page'] = page.value;
                if (defaultValues.sort !== sort.value) {
                    sortParams['sort'] = sort.value
                }

                filterStore.toUrlString(page.value, sortParams, false, true);
                const filterString = filterStore.toRawUrlString(page.value, sortParams);
                const category = categorySlug.value ? '/' + categorySlug.value : '';

                const response = await api().post<CustomProductResponseWithFilters>(
                    `/site/products${category}/${filterString}`,
                    {
                        lang: languageStore.currentIso(),
                        perPage: perPage.value,
                        filterOnly: filterOnly.value,
                    },
                    {signal: getProductRequest.abort.signal} as AxiosRequestConfig,
                )

                priceMax.value = response.data.maxPrice;
                if(response.data.currentPrice) {
                    currentPrice.value = response.data.currentPrice.value;
                }
                events.emit('initPriceFilter');
                setProducts(response.data);
                setCount(response.data.meta.total);
                filterStore.setFilterOptions(response.data.filters);
                resolve(response.data.data)
            } catch (e) {
                reject(e)
            }
        });
    }

    const getVariantProperties = async (variant: ProductVariantResponse) => {
        return new Promise(async (resolve, reject) => {
            if (getPropertiesRequest.abort !== null) {
                getPropertiesRequest.abort.abort()
            }
            getPropertiesRequest.abort = new AbortController()

            try {
                const response = await api().post(
                    `/site/product-variant/properties/`,
                    {
                        lang: languageStore.currentIso(),
                        product_variant_id: variant?.id,
                    },
                    {signal: getPropertiesRequest.abort.signal} as AxiosRequestConfig,
                );
                resolve(response.data);
            } catch (e) {
                reject(e);
                return [];
            }
        });
    }

    const setProducts = (newProducts: ProductResponse[]) => {
        products.value = newProducts;
    }

    const setSort = (newSort: string) => {
        sort.value = newSort;
    }

    const setNextPage = () => {
        if (hasMoreResults()) {
            page.value = page.value + 1;
        }
    }

    const setPerPage = (newPerPage: number) => {
        perPage.value = newPerPage;
    }

    const setCount = (newCount: number) => {
        productCount.value = newCount;
    }

    const setCategorySlug = (slug: string) => {
        categorySlug.value = slug;
    }

    const setCategoryName = (name: string) => {
        categoryName.value = name;
    }


    const setPage = (newPage: number) => {
        page.value = newPage;
    }

    const setSelectedVariant = (variant: ProductVariantResponse) => {
        selectedVariant.value = variant;

        events.emit('selectedVariantChanged', selectedVariant.value);
    }

    const setFilterOnly = (newFilterOnly: boolean) => {
        filterOnly.value = newFilterOnly;
    }

    return {
        events,
        products,
        sort,
        page,
        perPage,
        perPageOptions,
        productCount,
        categorySlug,
        categoryName,
        priceMin,
        priceMax,
        currentPrice,
        selectedVariant,
        layout,
        filterOnly,
        hasMoreResults,
        getProducts,
        getVariantProperties,
        setProducts,
        setSort,
        setNextPage,
        setPerPage,
        setCount,
        setCategorySlug,
        setCategoryName,
        setPage,
        setSelectedVariant,
        setFilterOnly,
    }
});
